package com.neurobs.js.view

import com.neurobs.util.{JsonPrinter, JsonUtil}

import scala.concurrent.Future

trait ResourcePaths {
	def getImagePath(localPath: String): String
}

trait Gui {
	def main: Container

	def modal: ModalView

	def sounds: SoundView

	def setCursor(t: Gui.CursorType.Value): Unit

	def resourcePaths: ResourcePaths

	def makeSimpleMessageBox(width: Int, title: String, description: String, dismissButtonCaption: String, customContent: Option[Container] = None): MessageBox = {
		makeMessageBox(width, title, description, List((dismissButtonCaption, MessageBoxResult.Ok)), customContent)
	}

	def makeMessageBox(width: Int, title: String, description: String, buttonCaptions: Seq[(String, MessageBoxResult.Value)], customContent: Option[Container] = None): MessageBox

	def makeFormattedText(content: String): Container

	def showModalForm(form: Form, processor: Form.Processor, processCancellable: Boolean, formCancel: Option[() => Unit] = None): Unit = {
		Form.showModal(form, modal, processor, processCancellable, formCancel)
	}

	def showModalMessage(width: Int, title: String, description: String, dismissButtonCaption: String, customContent: Option[Container] = None)(onComplete: (MessageBoxResult.Value) => Unit = (_) => Unit): Unit = {
		MessageBox.showModal(makeSimpleMessageBox(width, title, description, dismissButtonCaption, customContent), modal)(onComplete)
	}

	def showModalFormattedText(width: Int, title: String, content: String): Unit = {
		val contentString = JsonUtil.parseJsonString(content) match {
			case Left(_) => content
			case Right(json) =>
				json.printWith(JsonPrinter.jsonPrettyPrint)
		}
		showModalMessage(width = width, title = title, description = "", dismissButtonCaption = "Close", customContent = Some(makeFormattedText(contentString)))(_ => Unit)
	}

	def showModalCustomMessage(width: Int, title: String, description: String, buttonCaptions: Seq[(String, MessageBoxResult.Value)], customContent: Option[Container] = None)(onComplete: (MessageBoxResult.Value) => Unit = (_) => Unit): Unit = {
		MessageBox.showModal(makeMessageBox(width, title, description, buttonCaptions, customContent), modal)(onComplete)
	}

	def showConfirmationDialog(title: String, yesNoQuestion: String, width: Int = Gui.defaultMessageBoxWidth)(onYes: () => Unit): Unit = {
		val box = makeMessageBox(width = width, title = title, description = yesNoQuestion, buttonCaptions = Array(("No", MessageBoxResult.No), ("Yes", MessageBoxResult.Yes)))
		MessageBox.showModal(box, modal) {
			case MessageBoxResult.Yes => onYes()
			case _ =>
		}
	}

	def promptSingleValue[A](title: String = "", description: String = "", prompt: String, validate: (String) => Either[String, A], allowCancel: Boolean = true): Future[Option[A]]
}

class NullGui extends Gui {
	def main: Container = throw new RuntimeException("NullGui method called")

	def modal: ModalView = throw new RuntimeException("NullGui method called")

	def sounds: SoundView = throw new RuntimeException("NullGui method called")

	def setCursor(t: Gui.CursorType.Value): Unit = throw new RuntimeException("NullGui method called")

	def resourcePaths: ResourcePaths = throw new RuntimeException("NullGui method called")

	def makeMessageBox(width: Int, title: String, description: String, buttonCaptions: Seq[(String, MessageBoxResult.Value)], customContent: Option[Container] = None): MessageBox  = throw new RuntimeException("NullGui method called")

	def makeFormattedText(content: String): Container = throw new RuntimeException("NullGui method called")

	def promptSingleValue[A](title: String = "", description: String = "", prompt: String, validate: (String) => Either[String, A], allowCancel: Boolean = true): Future[Option[A]] = throw new RuntimeException("NullGui method called")
}

object Gui {
	object CursorType extends Enumeration {
		val Standard, Progress = Value
	}

	def toLocalTime(t: com.neurobs.util.SecondsTimestamp): String =
		new scala.scalajs.js.Date(t.instant.toEpochMilli).toLocaleTimeString()
	def toLocalDateTime(t: com.neurobs.util.SecondsTimestamp): String = {
		val dt = new scala.scalajs.js.Date(t.instant.toEpochMilli)
		s"${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}"
	}

	def validateNonEmpty(value: String): Either[String, String] =
		if (value.isEmpty) Left("Please enter a value.") else Right(value)

	val defaultMessageBoxWidth = 40
}
